import OauthService from "@/services/OauthService";
import { getHostAndDomain } from "./helper";
import { RouterRegistry } from "./routerRegistry";

export class DesktopController {
  private static instance: DesktopController;

  private constructor() {
    // Private constructor for singleton
  }

  static getInstance(): DesktopController {
    if (!DesktopController.instance) {
      DesktopController.instance = new DesktopController();
    }
    return DesktopController.instance;
  }

  private getUrlParams(): URLSearchParams {
    return new URLSearchParams(window.location.search);
  }

  /**
   * Check if this is a desktop authentication flow
   */
  isDesktopAuth(): boolean {
    const urlParams = this.getUrlParams();
    const source = urlParams.get("source");
    const deviceId = urlParams.get("deviceId");
    return source === "desktop" && !!deviceId;
  }

  /**
   * Get the device ID if present
   */
  getDeviceId(): string | null {
    const urlParams = this.getUrlParams();
    return urlParams.get("deviceId");
  }

  /**
   * Clear desktop auth parameters from URL
   */
  clearUrlParams(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete("source");
    url.searchParams.delete("deviceId");
    window.history.replaceState({}, "", url.toString());
  }

  async handleDesktopAuthentication(options?: {
    onFallback?: () => void | Promise<void>;
    onError?: (error: any) => void | Promise<void>;
  }): Promise<boolean> {
    if (!this.isDesktopAuth()) {
      return false;
    }

    const deviceId = this.getDeviceId();
    console.log("Desktop login detected with deviceId:", deviceId);

    try {
      const tokenResponse = await OauthService.generateMagicTokenForDesktop({
        deviceId: deviceId!,
      });

      // Redirect to desktop app
      await this.redirectToDesktopApp(tokenResponse.magicToken, {
        onFallback: options?.onFallback,
      });

      return true;
    } catch (error) {
      console.error("Failed to generate desktop token:", error);
      if (options?.onError) {
        await options.onError(error);
      }
      return false;
    }
  }

  async redirectToDesktopApp(
    magicToken: string,
    options?: {
      onFallback?: () => void | Promise<void>;
    }
  ): Promise<void> {
    const { onFallback } = options || {};

    // Build the desktop URL
    let desktopRedirectUrl = "";
    if (getHostAndDomain().domain.includes("leadconnectorhq")) {
      desktopRedirectUrl = `leadconnector://auth-success?magicToken=${magicToken}`;
    } else {
      desktopRedirectUrl = `highlevel://auth-success?magicToken=${magicToken}`;
    }

    // Show desktop launch page using Vue router
    this.showDesktopLaunchPage(desktopRedirectUrl, onFallback);
  }

  private showDesktopLaunchPage(
    desktopUrl: string,
    onFallback?: () => void | Promise<void>
  ) {
    try {
      const router = RouterRegistry.getRouter();
      if (router) {
        // Store the fallback callback and desktop URL securely in window object
        // instead of passing through URL query params
        (window as any).desktopLaunchFallback = onFallback || null;
        (window as any).desktopRedirectUrl = desktopUrl;

        router.push({
          name: "desktop_launch",
          query: {
            deviceId: this.getDeviceId() || "",
          },
        });
      } else {
        // Fallback if no router available
        this.directLaunch(desktopUrl, onFallback);
      }
    } catch (error) {
      console.error("Failed to navigate to desktop launch page:", error);
      // Fallback to direct method if router navigation fails
      this.directLaunch(desktopUrl, onFallback);
    }
  }

  private directLaunch(
    desktopUrl: string,
    onFallback?: () => void | Promise<void>
  ): void {
    window.location.href = desktopUrl;

    setTimeout(async () => {
      this.clearUrlParams();
      if (onFallback) {
        await onFallback();
      }
    }, 1000);
  }

  reset(): void {
    this.clearUrlParams();
  }
}

export const desktopController = DesktopController.getInstance();
